import { useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import {
  AlertCircle,
  BarChart3,
  Kanban,
  Plus,
  RefreshCw,
  Rows2,
  Search,
  Table2,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { Sheet, SheetContent, SheetTitle } from '@/components/atoms/sheet';
import { LeadsKanban } from '@/components/organisms/leads/LeadsKanban';
import { LeadAssigneeFilterSelect } from '@/components/organisms/leads/LeadAssigneeFilterSelect';
import {
  ContactsWorkspaceShell,
  EntityIndexHeader,
  EntityIndexLayout,
  useWorkspaceHeader,
} from '@/components/layouts';
import { EntityViewToggle } from '@/components/molecules';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  buildLeadListParams,
  cleanLeadListSearch,
  DEFAULT_LEAD_LIST_FILTERS,
  LeadListPanel,
  LeadSavedViewsMenu,
  LEAD_GRADE_FILTERS,
  LEAD_PRIORITY_FILTERS,
  LEAD_QUALIFICATION_FILTERS,
  LEAD_SLA_FILTERS,
  LEAD_SORT_OPTIONS,
  resolveLeadListFilters,
  type LeadListSearch,
  type LeadListFilters,
} from '@/components/organisms/leads/LeadListPanel';
import { LEAD_SOURCE_FILTERS } from '@/lib/constants/lead-sources';
import { LeadTableView } from '@/components/organisms/leads/LeadTableView';
import { Fullbleed } from '@/contexts/fullbleed';
import { useLeadStages } from '@/hooks/leads/useLeadStages';
import { useLeadPipelineSummary } from '@/hooks/leads/useLeadPipelineSummary';
import { useHasPermission } from '@/hooks/usePermissions';

type ViewMode = 'split' | 'table' | 'board';
type LeadRouteSearch = LeadListSearch;

const NUMBER_OPERATORS = ['eq', 'gt', 'lt', 'gte', 'lte'];
const SOURCE_VALUES = new Set<string>(LEAD_SOURCE_FILTERS.map((option) => option.value));
const PRIORITY_VALUES = new Set<string>(LEAD_PRIORITY_FILTERS.map((option) => option.value));
const GRADE_VALUES = new Set<string>(LEAD_GRADE_FILTERS.map((option) => option.value));
const QUALIFICATION_VALUES = new Set<string>(
  LEAD_QUALIFICATION_FILTERS.map((option) => option.value),
);
const SLA_VALUES = new Set<string>(LEAD_SLA_FILTERS.map((option) => option.value));

function asString(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getStoredView(): ViewMode {
  try {
    const value = localStorage.getItem('leads-view');
    if (value === 'table' || value === 'board') return value;
  } catch {
    /* no-op */
  }
  return 'split';
}

export const Route = createFileRoute('/_dashboard/dashboard/leads')({
  validateSearch: (search: Record<string, unknown>): LeadRouteSearch => {
    const source = asString(search.source, DEFAULT_LEAD_LIST_FILTERS.source);
    const priority = asString(search.priority, DEFAULT_LEAD_LIST_FILTERS.priority);
    const leadGrade = asString(search.leadGrade, DEFAULT_LEAD_LIST_FILTERS.leadGrade);
    const qualificationStatus = asString(
      search.qualificationStatus,
      DEFAULT_LEAD_LIST_FILTERS.qualificationStatus,
    );
    const slaStatus = asString(search.slaStatus, DEFAULT_LEAD_LIST_FILTERS.slaStatus);
    const estimatedValueOp = asString(
      search.estimatedValueOp,
      DEFAULT_LEAD_LIST_FILTERS.estimatedValueOp,
    );

    return {
      q: asString(search.q, DEFAULT_LEAD_LIST_FILTERS.q),
      stage: asString(search.stage, DEFAULT_LEAD_LIST_FILTERS.stage),
      // Free-form like `stage`: the value is a user id, so there is no closed
      // set to validate against here. A stale id from a bookmark yields an
      // empty page, exactly as a stale stage id does.
      assignedTo: asString(search.assignedTo, DEFAULT_LEAD_LIST_FILTERS.assignedTo),
      source: SOURCE_VALUES.has(source)
        ? (source as LeadListFilters['source'])
        : DEFAULT_LEAD_LIST_FILTERS.source,
      priority: PRIORITY_VALUES.has(priority)
        ? (priority as LeadListFilters['priority'])
        : DEFAULT_LEAD_LIST_FILTERS.priority,
      leadGrade: GRADE_VALUES.has(leadGrade)
        ? (leadGrade as LeadListFilters['leadGrade'])
        : DEFAULT_LEAD_LIST_FILTERS.leadGrade,
      qualificationStatus: QUALIFICATION_VALUES.has(qualificationStatus)
        ? qualificationStatus
        : DEFAULT_LEAD_LIST_FILTERS.qualificationStatus,
      slaStatus: SLA_VALUES.has(slaStatus)
        ? (slaStatus as LeadListFilters['slaStatus'])
        : DEFAULT_LEAD_LIST_FILTERS.slaStatus,
      estimatedValueOp: NUMBER_OPERATORS.includes(estimatedValueOp)
        ? (estimatedValueOp as LeadListFilters['estimatedValueOp'])
        : DEFAULT_LEAD_LIST_FILTERS.estimatedValueOp,
      estimatedValueValue: asString(
        search.estimatedValueValue,
        DEFAULT_LEAD_LIST_FILTERS.estimatedValueValue,
      ),
      page: Math.max(1, asNumber(search.page, DEFAULT_LEAD_LIST_FILTERS.page)),
      sort: Math.min(
        LEAD_SORT_OPTIONS.length - 1,
        Math.max(0, asNumber(search.sort, DEFAULT_LEAD_LIST_FILTERS.sort)),
      ),
      dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
      dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    };
  },
  component: LeadsLayout,
});

function LeadsLayout() {
  const router = useRouter();
  const { location } = useRouterState();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const routeSearch = Route.useSearch();
  const filters = useMemo(() => resolveLeadListFilters(routeSearch), [routeSearch]);
  const [boardMode, setBoardMode] = useState(() => getStoredView() === 'board');
  const canCreateLeads = useHasPermission('leads:create');

  const isNewRoute = /\/dashboard\/leads\/new\/?$/.test(location.pathname);
  const isEditRoute = /\/dashboard\/leads\/[^/]+\/edit\/?$/.test(location.pathname);
  const isReportsRoute = /\/dashboard\/leads\/reports\/?$/.test(location.pathname);
  const routeLeadId = location.pathname.match(/\/dashboard\/leads\/([^/]+)/)?.[1];
  const selectedId =
    routeLeadId && routeLeadId !== 'new' && routeLeadId !== 'reports' ? routeLeadId : undefined;
  const canViewReports = useHasPermission('leads:view');
  const hasChildRoute = isNewRoute || Boolean(selectedId);
  const isFormRoute = isNewRoute || isEditRoute;
  const listSearch = cleanLeadListSearch(filters);
  const detailSearch = { ...listSearch, tab: 'overview' as const };

  function handleSelect(leadId: string) {
    void router.navigate({
      to: '/dashboard/leads/$leadId',
      params: { leadId },
      search: detailSearch,
    });
  }

  function handleFiltersChange(patch: Partial<LeadListFilters>) {
    const next = resolveLeadListFilters({ ...filters, ...patch });
    void router.navigate({
      to: '/dashboard/leads',
      search: cleanLeadListSearch(next),
      replace: true,
    });
  }

  function handleFiltersReplace(nextFilters: Partial<LeadListFilters>) {
    const next = resolveLeadListFilters({
      ...DEFAULT_LEAD_LIST_FILTERS,
      ...nextFilters,
      page: 1,
    });
    void router.navigate({
      to: '/dashboard/leads',
      search: cleanLeadListSearch(next),
      replace: true,
    });
  }

  function resetToIndex() {
    void router.navigate({ to: '/dashboard/leads', search: listSearch });
  }

  function navigateToNewLead() {
    void router.navigate({ to: '/dashboard/leads/new', search: listSearch });
  }

  function showBoard() {
    setBoardMode(true);
    try {
      localStorage.setItem('leads-view', 'board');
    } catch {
      /* no-op */
    }
    if (hasChildRoute) resetToIndex();
  }

  if (isFormRoute) return <Outlet />;

  // Reports render full-page (their own shell), like the other domains.
  if (isReportsRoute) return <Outlet />;

  // Board view: keep the board mounted even when a lead is selected, and open
  // the lead detail in a right-side drawer over the board (mirrors the Table
  // view's sheet) instead of falling through to the split/list layout. The
  // form/reports routes are handled by the early returns above, so they never
  // reach this branch.
  if (boardMode) {
    // Below md (<768px), show the selected lead full-page — mirroring the
    // split/table mobile path (EntityIndexLayout's mobileContent, which also
    // cuts over at 768px) — instead of a right drawer. A narrow-viewport Sheet
    // would be ~full-width with `showCloseButton={false}` and little/no exposed
    // overlay to tap, leaving no way back to the board. Only at ≥768px (where the
    // 620px drawer leaves ample overlay to click, and Escape works) do we keep
    // the drawer — matching why the table-view sheet safely hides its close
    // button. Gate on useIsDesktop (768px), NOT useIsMobile (639px), so the
    // 640–767px band isn't left with an undismissable drawer.
    if (!isDesktop && selectedId) {
      return (
        <ContactsWorkspaceShell>
          <Outlet />
        </ContactsWorkspaceShell>
      );
    }

    return (
      <ContactsWorkspaceShell>
        <LeadsBoardView
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onFiltersReplace={handleFiltersReplace}
          onOpenLead={handleSelect}
          canCreate={canCreateLeads}
          onNewLead={() => navigateToNewLead()}
          onViewChange={(mode) => {
            setBoardMode(false);
            try {
              localStorage.setItem('leads-view', mode);
            } catch {
              /* no-op */
            }
          }}
        />
        <Sheet
          open={Boolean(selectedId)}
          onOpenChange={(open) => {
            if (!open) resetToIndex();
          }}
        >
          <SheetContent
            side="right"
            className="w-[620px] overflow-y-auto p-0 sm:max-w-[620px]"
            showCloseButton={false}
          >
            <SheetTitle className="sr-only">Lead Detail</SheetTitle>
            <Outlet />
          </SheetContent>
        </Sheet>
      </ContactsWorkspaceShell>
    );
  }

  return (
    <ContactsWorkspaceShell>
      <LeadsHeaderSlot
        hidden={isFormRoute || (isMobile && hasChildRoute)}
        canCreate={canCreateLeads}
        canViewReports={canViewReports}
        listSearch={listSearch}
      />
      <EntityIndexLayout
        title="Leads"
        storageKey="leads-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={resetToIndex}
        extraViewOptions={[
          { value: 'board', icon: Kanban, title: 'Leads board view', label: 'Board' },
        ]}
        onExtraViewChange={(value) => {
          if (value === 'board') showBoard();
        }}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass={
          isFormRoute ? 'w-[95vw] sm:max-w-[1200px]' : 'w-[620px] sm:max-w-[620px]'
        }
        hideListInSplit={isFormRoute}
        splitList={() => (
          <div className="flex h-full flex-col">
            <LeadListPanel
              selectedId={selectedId}
              onSelect={handleSelect}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onFiltersReplace={handleFiltersReplace}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <LeadTableView
            onOpen={(leadId) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/leads/$leadId',
                    params: { leadId },
                    search: detailSearch,
                  }),
              )
            }
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden flex flex-col flex-1 min-h-0">
            {hasChildRoute ? (
              <Outlet />
            ) : (
              <LeadListPanel
                selectedId={selectedId}
                onSelect={handleSelect}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onFiltersReplace={handleFiltersReplace}
              />
            )}
          </div>
        }
      />
    </ContactsWorkspaceShell>
  );
}

// Injects the Add/Reports actions into the global workspace header (mirrors
// Jobs' JobsNewButtonSlot) instead of a per-view title row.
function LeadsHeaderSlot({
  hidden,
  canCreate,
  canViewReports,
  listSearch,
}: {
  hidden: boolean;
  canCreate: boolean;
  canViewReports: boolean;
  listSearch: LeadRouteSearch;
}) {
  const newButton = useMemo(() => {
    if (hidden || !canCreate) return null;
    return (
      <Button size="sm" title="Add lead" asChild>
        <Link to="/dashboard/leads/new" search={listSearch}>
          <Plus size={14} />
          Add lead
        </Link>
      </Button>
    );
  }, [hidden, canCreate, listSearch]);

  const toolbarActions = useMemo(() => {
    if (hidden || !canViewReports) return null;
    return (
      <Button variant="outline" size="sm" title="Lead reports" asChild>
        <Link to="/dashboard/leads/reports">
          <BarChart3 size={14} />
          Reports
        </Link>
      </Button>
    );
  }, [hidden, canViewReports]);

  useWorkspaceHeader({ newButton, toolbarActions }, [newButton, toolbarActions]);
  return null;
}

interface LeadsBoardViewProps {
  filters: LeadListFilters;
  onFiltersChange: (patch: Partial<LeadListFilters>) => void;
  onFiltersReplace: (filters: Partial<LeadListFilters>) => void;
  onOpenLead: (leadId: string) => void;
  canCreate: boolean;
  onNewLead: () => void;
  onViewChange: (mode: 'split' | 'table') => void;
}

function LeadsBoardView({
  filters,
  onFiltersChange,
  onFiltersReplace,
  onOpenLead,
  canCreate,
  onNewLead,
  onViewChange,
}: LeadsBoardViewProps) {
  const router = useRouter();
  const stagesQuery = useLeadStages();
  const allStages = useMemo(() => stagesQuery.data ?? [], [stagesQuery.data]);
  // NOT-515: honor a `stage` filter carried in the shared URL / saved-view state.
  // The board's columns ARE the stages, so a selected stage limits the board to
  // that one column (and the header total to that stage) rather than being
  // silently ignored — matching the pre-pagination board, whose single /leads
  // query was stage-filtered.
  const stages = useMemo(
    () =>
      filters.stage !== 'all' ? allStages.filter((stage) => stage.id === filters.stage) : allStages,
    [allStages, filters.stage],
  );
  // NOT-514 #2: the board renders one column per stage and paginates each column
  // independently, so it no longer fetches a single 100-lead slice. `boardParams`
  // carries the active filters minus `stage` — the visible `stages` above already
  // encode the stage filter, and each column overrides `stage` with its own id,
  // so a per-request `stage` would be redundant.
  const boardParams = useMemo(() => {
    const params = buildLeadListParams({ ...filters, page: 1 });
    delete params.stage;
    return params;
  }, [filters]);
  const summaryQuery = useLeadPipelineSummary(boardParams);
  const summary = useMemo(() => summaryQuery.data ?? [], [summaryQuery.data]);
  // Sum only the visible stages so the header total honors the stage filter.
  const total = useMemo(() => {
    const visibleStageIds = new Set(stages.map((stage) => stage.id));
    return summary
      .filter((entry) => visibleStageIds.has(entry.stageId))
      .reduce((sum, entry) => sum + entry.count, 0);
  }, [summary, stages]);
  const isLoading = summaryQuery.isLoading || stagesQuery.isLoading;

  const viewToggle = (
    <EntityViewToggle
      value="board"
      onChange={(mode) => {
        if (mode === 'split' || mode === 'table') onViewChange(mode);
      }}
      options={[
        { value: 'split', icon: Rows2, title: 'Leads split view' },
        { value: 'table', icon: Table2, title: 'Leads table view' },
        { value: 'board', icon: Kanban, title: 'Leads board view' },
      ]}
    />
  );

  return (
    <>
      <Fullbleed />
      <div className="flex h-full flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
        <EntityIndexHeader
          title={`Leads${!summaryQuery.isLoading ? ` (${total})` : ''}`}
          actions={
            <>
              <LeadSavedViewsMenu filters={filters} onApply={onFiltersReplace} />
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  value={filters.q}
                  onChange={(event) => onFiltersChange({ q: event.target.value, page: 1 })}
                  placeholder="Search leads..."
                  className="h-8 w-48 pl-8 text-sm"
                />
              </div>
              <LeadAssigneeFilterSelect
                value={filters.assignedTo}
                onValueChange={(value) => onFiltersChange({ assignedTo: value, page: 1 })}
              />
              {viewToggle}
              {canCreate ? (
                <Button size="icon-sm" title="New lead" onClick={onNewLead}>
                  <Plus size={14} />
                  <span className="sr-only">New lead</span>
                </Button>
              ) : null}
            </>
          }
        />

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {summaryQuery.isError ? (
            <div className="flex h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
              <AlertCircle size={32} />
              <p className="text-sm">Failed to load leads</p>
              <Button variant="outline" size="sm" onClick={() => void summaryQuery.refetch()}>
                <RefreshCw size={14} className="mr-1.5" />
                Retry
              </Button>
            </div>
          ) : isLoading ? (
            <div className="flex gap-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="min-w-[260px] flex-1 space-y-2">
                  <Skeleton className="h-8 w-full" />
                  {Array.from({ length: 3 }).map((__, rowIndex) => (
                    <Skeleton key={rowIndex} className="h-24 w-full" />
                  ))}
                </div>
              ))}
            </div>
          ) : stages.filter((stage) => stage.isActive).length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed text-center text-muted-foreground">
              <AlertCircle size={32} />
              <div>
                <p className="text-sm font-medium text-foreground">No pipeline stages configured</p>
                <p className="text-xs">Go to Settings → Leads to add stages.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void router.navigate({ to: '/dashboard/settings/leads' })}
              >
                Open lead settings
              </Button>
            </div>
          ) : (
            <LeadsKanban
              stages={stages.filter((stage) => stage.isActive)}
              params={boardParams}
              summary={summary}
              onOpenLead={onOpenLead}
            />
          )}
        </div>
      </div>
    </>
  );
}
